import { ColumnConfig, ColumnConfigOptions, ColumnText, DateFormatter, LodashExtractor, LodashSeriesEnricherMapper, MappedValueFormatter, NumberFormatter, StoreNestedFilterExtractor, TabConfig, TablePerPortfolioSeriesExtractorConfig } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';

import { MODEL_FAMILY_COLUMN_CONFIG } from './family-column.config';


export const MINI_COMPARE_DATA_CONFIG = new TabConfig(
	'mini.compare.data',
	null,
	new TablePerPortfolioSeriesExtractorConfig(
		new LodashSeriesEnricherMapper(['families', 'familyOverview', 'familyPerformance'],
			['families', 'familyOverview', 'familyPerformance'], 'families.models',
			(portfolio: any, data: any) => {
				return {
					portfolioData: portfolio,
					...data?.families,
					...(data?.familyOverview?.models ?? []).find(model => model.id === portfolio.id),
					...(data?.familyPerformance?.models ?? []).find(model => model.id === portfolio.id),
					...(data?.familyPerformance?.modelsBenchmark ?? []).find(model => model.id === portfolio.id)
				};
			}),
		null,
		[
			// families data
			ColumnConfig.create({
				id: 'id',
				extractor: new LodashExtractor('id', ''),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'preferences',
				extractor: new LodashExtractor('preferences', '-'),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				label: new ColumnText('OBJECTIVE', 'Objective'),
				id: 'objective',
				extractor: new LodashExtractor('marketingDescription', '-')
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'openArchitecture',
				extractor: new LodashExtractor('openArchitecture', '-'),
				labelFormatter: new MappedValueFormatter({
					'closed': 'No',
					'open': 'Yes'
				}),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'vehicle',
				extractor: new LodashExtractor('vehicle', '-'),
				labelFormatter: new MappedValueFormatter({
					'etf_&_mf': 'ETFs and MFs',
					'etf_and_mf': 'ETFs and MFs',
					'etf_only': 'ETF only',
					'mf_only': 'MF only'
				}),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				label: new ColumnText('TRADE_FREQUENCY', 'Trade Frequency'),
				id: 'tradeFrequency',
				extractor: new LodashExtractor('tradeFrequency', '-'),
				labelFormatter: new MappedValueFormatter({
					'dynamic_(4-8_trades_per_year)': 'Dynamic <br />(4-8 trades/year)',
					'dynamic_(4-6_trades_per_year)': 'Dynamic <br />(4-6 trades/year)',
					'tactical_(more_than_8_trades_per_year)': 'Tactical <br />(> 8 trades/year)',
					'strategic_(less_than_3_trades_per_year)': 'Strategic <br />(< 3 trades/year)',
					'dynamic_4-8_trades_per_year': 'Dynamic <br />(4-8 trades/year)',
					'dynamic_4-6_trades_per_year': 'Dynamic <br />(4-6 trades/year)',
					'tactical_more_than_8_trades_per_year': 'Tactical <br />(> 8 trades/year)',
					'strategic_less_than_3_trades_per_year': 'Strategic <br />(< 3 trades/year)'
				})
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'modelCategoryType',
				extractor: new LodashExtractor('modelCategoryType', '-'),
				visible: false,
				labelFormatter: new MappedValueFormatter({
					'target_allocation_models': 'Target Allocation Models',
					'global_allocation_models': 'Global Allocation Models',
					'growth_and_income_models': 'Growth and Income Models',
					'asset_income_models': 'Asset Income Models',
					'lifepath_models': 'LifePath Models'
				})
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'riskProfile',
				extractor: new LodashExtractor('portfolioData.riskProfile', '-'),
				labelFormatter: new MappedValueFormatter({
					'conservative': 'Conservative',
					'moderate_conservative': 'Moderate Conservative',
					'moderate': 'Moderate',
					'moderate_aggressive': 'Moderate Aggressive',
					'aggressive': 'Aggressive'
				})
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'aladdinTicker',
				extractor: new LodashExtractor('aladdinTicker', '-'),
				visible: false
			} as ColumnConfigOptions<LodashExtractor>),
			// overview data
			ColumnConfig.create({
				id: 'expenseRatio',
				extractor: new StoreNestedFilterExtractor(
					['familyOverview'],
					'characteristics',
					[{ propertyToCompare: 'name', comparedValue: 'expenseRatio', desiredValueExpression: 'total' }]
				),
				labelFormatter: new NumberFormatter(NumberFormatter.PERCENT)
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			// TODO rohyadav: to be updated after confirmation data mapping
			ColumnConfig.create({
				id: 'grossExpenseRatio',
				extractor: new StoreNestedFilterExtractor(
					['familyOverview'],
					'characteristics',
					[{ propertyToCompare: 'name', comparedValue: 'grossExpenseRatio', desiredValueExpression: 'total' }]
				),
				labelFormatter: new NumberFormatter(NumberFormatter.PERCENT)
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'estimatedRisk',
				extractor: new LodashExtractor('risk.total', ''),
				labelFormatter: NumberFormatter.create({ suffix: NumberFormatter.PERCENT, multiplier: 100 })
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'ytdReturn',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ suffix: NumberFormatter.PERCENT, multiplier: 100 })
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'estimatedRiskAsOfDate',
				extractor: new LodashExtractor('analysisDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT)
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'expenseRatioAsOfDate',
				extractor: new LodashExtractor('analysisDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT)
			} as ColumnConfigOptions<LodashExtractor>),
			ColumnConfig.create({
				id: 'ytdReturnAsOfDate',
				extractor: new LodashExtractor('performance.exPostMetrics[0].interval.stopDate', ''),
				labelFormatter: new DateFormatter(DateUtil.MODELS_US_DATE_FORMAT)
			} as ColumnConfigOptions<LodashExtractor>),
			// performance data
			ColumnConfig.create({
				id: 'yearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ suffix: NumberFormatter.PERCENT, multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'customNetYearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'performance.netExPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ suffix: NumberFormatter.PERCENT, multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>),
			ColumnConfig.create({
				id: 'benchmarkYearToDate',
				extractor: new StoreNestedFilterExtractor(
					['familyPerformance'],
					'benchmarkAnalysis.performance.exPostMetrics[0].metrics',
					[{ propertyToCompare: 'name', comparedValue: 'totalReturnYtd', desiredValueExpression: 'metricValue' }]
				),
				labelFormatter: NumberFormatter.create({ suffix: NumberFormatter.PERCENT, multiplier: 100 }),
				visible: true
			} as ColumnConfigOptions<StoreNestedFilterExtractor>)
		],
		MODEL_FAMILY_COLUMN_CONFIG
	)
);
